import type { ColorModeProviderProps } from "@chakra-ui/react";
import {
  COLOR_MODE_COOKIE,
  isAppearanceMode,
  readCookie,
  writeCookie,
  type AppearanceMode,
} from "./cookies";

/**
 * Chakra colour-mode manager backed by the `chakra-ui-color-mode` cookie.
 *
 * Spec: specs/active/2026-08-29-website-design-system-adoption (Phase 2, Rev 3).
 *
 * How Chakra 2.1.12 uses it (node_modules/@chakra-ui/color-mode, ColorModeProvider):
 *  - `useState(() => manager.type === "cookie" && manager.ssr ? manager.get(init) : init)`
 *    — with `ssr: true` the FIRST render already has the right mode, so the ~60
 *    `useColorModeValue` sites never render light and then flip.
 *  - a mount effect calls `setColorMode(manager.get())`, and `setColorMode`
 *    always ends in `manager.set(value)`. That call is NOT a visitor's choice:
 *    persisting it would freeze the OS preference of the first visit forever.
 *    So `set` writes only when the appearance control armed it just before
 *    calling `setColorMode` (see `armExplicitColorModeWrite`).
 *
 * `get` order: cookie (whitelisted) → the `data-theme` the pre-paint script put
 * on <html> (cookie, migrated localStorage, or the OS preference) → the OS
 * preference directly (script blocked) → Chakra's fallback.
 *
 * Accepted limit: a visitor whose browser blocks cookies loses persistence —
 * a choice applies for the current page life only and the next load follows
 * the OS again. There is deliberately no localStorage fallback (one store,
 * one whitelist, one pre-paint read).
 */

// Chakra 2.6 does not re-export its StorageManager type; derive it.
type StorageManager = NonNullable<ColorModeProviderProps["colorModeManager"]>;

let explicitWriteArmed = false;

/** Call immediately before `setColorMode` when the VISITOR picked a mode. */
export const armExplicitColorModeWrite = (): void => {
  explicitWriteArmed = true;
};

const systemMode = (): AppearanceMode | undefined => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return undefined;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const colorModeManager: StorageManager = {
  type: "cookie",
  ssr: true,
  get(init) {
    const stored = readCookie(COLOR_MODE_COOKIE);
    if (isAppearanceMode(stored)) return stored;
    if (typeof document !== "undefined") {
      const painted = document.documentElement.getAttribute("data-theme");
      if (isAppearanceMode(painted)) return painted;
    }
    return systemMode() ?? init;
  },
  set(value) {
    const armed = explicitWriteArmed;
    explicitWriteArmed = false;
    if (!armed || !isAppearanceMode(value)) return;
    writeCookie(COLOR_MODE_COOKIE, value);
  },
};
